/**
 * Chocowi Document Generator
 * Professional letterhead documents: Invoice, Packing Slip, Account Statement, Payout Statement, Expense Statement
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, calculateSellerAmount, orderSellerFeePercent } from '../data/constants';

const BRAND_DARK = [44, 24, 16];      // #2C1810
const BRAND_ACCENT = [139, 69, 19];   // #8B4513
const BG_WARM = [252, 250, 248];      // #FCFAF8
const BORDER_LIGHT = [232, 224, 216]; // #E8E0D8
const TEXT_MUTED = [100, 100, 100];
const MARGIN = 16;
const PAGE_WIDTH = 210;

const loadLogoAsBase64 = async (logoPath) => {
  const tryLoad = async (path) => {
    const url = path.startsWith('http') ? path : `${window.location.origin}${path.startsWith('/') ? path : '/' + path}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to load');
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  try {
    return await tryLoad(logoPath);
  } catch {
    try {
      const publicLogo = `${process.env.PUBLIC_URL || ''}/chocowi-logo.png`;
      return await tryLoad(publicLogo);
    } catch {
      return null;
    }
  }
};

const addLetterhead = (doc, logoBase64, options = {}) => {
  const { title, subtitle } = options;
  const headerH = 48;
  doc.setFillColor(...BG_WARM);
  doc.rect(0, 0, PAGE_WIDTH, headerH, 'F');

  const logoW = 17;
  const logoH = 16;
  const logoY = 8;

  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', MARGIN, logoY, logoW, logoH);
  }

  const textStartX = logoBase64 ? MARGIN + logoW + 8 : MARGIN;
  doc.setFontSize(14);
  doc.setTextColor(...BRAND_DARK);
  doc.setFont('helvetica', 'bold');
  doc.text('Chocowi', textStartX, logoY + 6);

  doc.setFontSize(8);
  doc.setTextColor(...TEXT_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.text('Marketplace for Artisans & Craftspeople', textStartX, logoY + 12);

  if (title) {
    doc.setFontSize(12);
    doc.setTextColor(...BRAND_DARK);
    doc.setFont('helvetica', 'bold');
    doc.text(title, MARGIN, 32);
  }
  if (subtitle) {
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, MARGIN, 38);
  }

  doc.setDrawColor(...BRAND_ACCENT);
  doc.setLineWidth(1);
  doc.line(MARGIN, headerH, PAGE_WIDTH - MARGIN, headerH);
};

const addFooter = (doc, text) => {
  doc.setDrawColor(...BORDER_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, 278, PAGE_WIDTH - MARGIN, 278);
  doc.setFontSize(8);
  doc.setTextColor(...TEXT_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.text(text, MARGIN, 283);
};

const tableTheme = {
  headStyles: { fillColor: BRAND_DARK, textColor: 255, fontStyle: 'bold', fontSize: 9 },
  styles: { fontSize: 9, cellPadding: 4 },
  alternateRowStyles: { fillColor: [252, 250, 248] },
};

const getSelectedOptionsDescription = (order) => {
  const { pricingType, selectedOptions, listingType } = order.productInfo || {};
  const isService = listingType === 'service';
  const typeLabel = isService ? 'Service' : 'Product';
  if (order.orderType === 'deposit') return `[Custom ${typeLabel} • Deposit Based]`;
  if (pricingType === 'custom') return `[Custom ${typeLabel}]`;
  if (!pricingType || !selectedOptions) return typeLabel;
  if (pricingType === 'fixed') {
    const pkg = selectedOptions.fixedPricing?.packageName;
    return pkg ? `Package: ${pkg}` : typeLabel;
  }
  if (pricingType === 'variable') {
    const options = selectedOptions.variablePricing?.variables
      ?.map((v) => `${v.name}: ${v.selectedOption?.name}`)
      .filter(Boolean)
      .join(' • ');
    return options || typeLabel;
  }
  return typeLabel;
};

const getOrderLineItems = (order) => {
  const items = [];
  const desc = getSelectedOptionsDescription(order);
  const baseQty = order.summary?.quantity || 1;
  const unitPrice = order.summary?.price || 0;
  items.push([order.productInfo?.title || 'Item', desc, baseQty, unitPrice, (unitPrice * baseQty)]);
  (order.addedServices || []).forEach((svc) => {
    const price = Number(svc.price) || 0;
    items.push([`+ ${svc.name}`, svc.description || '', 1, price, price]);
  });
  return items;
};

export const generateInvoice = async (order, { currency, exchangeRate, logoPath, isSeller, feesObj = {} }) => {
  const doc = new jsPDF();
  const logoBase64 = logoPath ? await loadLogoAsBase64(logoPath) : null;

  addLetterhead(doc, logoBase64, {
    title: 'INVOICE',
    subtitle: `Invoice #${order.customOrderId} • ${new Date(order.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
  });

  let yPos = 58;

  const buyerName = order.buyerInfo?.fullName || 'N/A';
  const sellerName = order.sellerId?.displayName || order.sellerId?.fullName || 'N/A';

  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO', MARGIN, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(buyerName, MARGIN, yPos + 7);
  doc.setFontSize(9);
  doc.text(order.buyerInfo?.email || '', MARGIN, yPos + 14);
  doc.text(order.buyerInfo?.phone || '', MARGIN, yPos + 21);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('FROM', 110, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(sellerName, 110, yPos + 7);
  const addr = order.deliveryAddress;
  if (addr) {
    doc.setFontSize(9);
    const addrLine = [addr.address, addr.unitNumber].filter(Boolean).join(', ');
    const cityLine = [addr.city, addr.state?.name, addr.postalCode || addr.zipCode].filter(Boolean).join(', ');
    doc.text(addrLine || '', 110, yPos + 14);
    if (cityLine) doc.text(cityLine, 110, yPos + 20);
  }

  yPos += 38;

  const tableData = getOrderLineItems(order).map(([item, desc, qty, unit, amt]) =>
    [item, desc, qty, formatCurrency(unit, currency, exchangeRate), formatCurrency(amt, currency, exchangeRate)]
  );

  autoTable(doc, {
    startY: yPos,
    head: [['Item', 'Description', 'Qty', 'Unit Price', 'Amount']],
    body: tableData,
    theme: 'plain',
    ...tableTheme,
    columnStyles: { 0: { cellWidth: 48 }, 1: { cellWidth: 48 }, 2: { cellWidth: 18 }, 3: { cellWidth: 35 }, 4: { cellWidth: 35 } }
  });

  yPos = doc.lastAutoTable.finalY + 12;
  const summary = order.summary || {};
  const rightX = 140;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Subtotal`, rightX - 50, yPos);
  doc.text(formatCurrency(summary.subtotal, currency, exchangeRate), rightX, yPos);
  doc.text(`Delivery`, rightX - 50, yPos + 7);
  doc.text(formatCurrency(summary.deliveryFee, currency, exchangeRate), rightX, yPos + 7);
  doc.text(`Service Fee`, rightX - 50, yPos + 14);
  doc.text(formatCurrency(summary.serviceFee, currency, exchangeRate), rightX, yPos + 14);
  if (!isSeller) {
    doc.text(`Tax`, rightX - 50, yPos + 21);
    doc.text(formatCurrency(summary.tax, currency, exchangeRate), rightX, yPos + 21);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  let totalAmount = summary.total;
  if (isSeller) {
    totalAmount = summary.sellerGot != null ? summary.sellerGot : calculateSellerAmount((summary.subtotal || 0) + (summary.deliveryFee || 0), orderSellerFeePercent(summary, feesObj.seller || 0));
  }
  const totalY = yPos + (isSeller ? 24 : 32);
  doc.text(isSeller ? 'Net Amount' : 'Total', rightX - 50, totalY);
  doc.text(formatCurrency(totalAmount, currency, exchangeRate), rightX, totalY);

  let reqY = totalY + 14;
  if (order.buyerRequirements?.length > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Buyer Requirements', MARGIN, reqY);
    doc.setFont('helvetica', 'normal');
    order.buyerRequirements.forEach((req, i) => {
      reqY += 6;
      doc.setFontSize(8);
      doc.text(`${i + 1}. ${req.question || ''}`, MARGIN, reqY);
      if (req.answer) doc.text(`   Answer: ${req.answer}`, MARGIN + 4, reqY + 5);
      reqY += 8;
    });
  }

  addFooter(doc, 'Thank you for your business. This is a computer-generated invoice from Chocowi.');

  doc.save(`Chocowi-Invoice-${order.customOrderId}.pdf`);
};

export const generatePackingSlip = async (order, { currency, exchangeRate, logoPath }) => {
  const doc = new jsPDF();
  const logoBase64 = logoPath ? await loadLogoAsBase64(logoPath) : null;

  addLetterhead(doc, logoBase64, {
    title: 'PACKING SLIP',
    subtitle: `Order #${order.customOrderId} • ${new Date(order.createdAt).toLocaleDateString('en-US')}`
  });

  let yPos = 58;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Ship To:', 14, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(order.buyerInfo?.fullName || 'N/A', 14, yPos + 6);
  const addr = order.deliveryAddress;
  if (addr) {
    doc.text(`${addr.address || ''}`, 14, yPos + 12);
    doc.text(`${addr.city || ''}, ${addr.state?.name || ''} ${addr.country?.name || ''}`, 14, yPos + 18);
    if (addr.note) doc.text(`Note: ${addr.note}`, 14, yPos + 26);
  }

  yPos += (addr?.note ? 40 : 32);

  const slipRows = getOrderLineItems(order).map(([item, desc, qty]) => [item, desc, qty]);
  autoTable(doc, {
    startY: yPos,
    head: [['Product', 'Options', 'Qty']],
    body: slipRows,
    theme: 'plain',
    ...tableTheme,
  });

  yPos = doc.lastAutoTable.finalY + 12;
  if (order.deliveryType) {
    doc.setFontSize(9);
    doc.text(`Delivery: ${order.deliveryType === 'delivery' ? 'Shipping' : 'Pickup'}`, MARGIN, yPos);
    if (order.requestedDeliveryDate) {
      doc.text(`Requested: ${new Date(order.requestedDeliveryDate).toLocaleDateString()}`, MARGIN, yPos + 6);
    }
  }
  if (order.buyerRequirements?.length > 0) {
    yPos += 14;
    doc.setFont('helvetica', 'bold');
    doc.text('Buyer Requirements', MARGIN, yPos);
    doc.setFont('helvetica', 'normal');
    order.buyerRequirements.forEach((req, i) => {
      yPos += 6;
      doc.setFontSize(8);
      doc.text(`${i + 1}. ${req.question || ''}`, MARGIN, yPos);
      if (req.answer) doc.text(`   Answer: ${req.answer}`, MARGIN + 4, yPos + 5);
      yPos += 6;
    });
  }

  addFooter(doc, 'Pack this order with care. Chocowi');

  doc.save(`Chocowi-PackingSlip-${order.customOrderId}.pdf`);
};

const getDateRangeLabel = (period, startDate, endDate) => {
  if (period === 'custom' && startDate && endDate) {
    return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
  }
  const labels = { week: 'Last 7 Days', month: 'Last 30 Days', '3month': 'Last 3 Months', '6month': 'Last 6 Months', year: 'Last 12 Months', lifetime: 'Lifetime' };
  return labels[period] || period;
};

export const generateAccountStatement = async (orders, sellerInfo, { currency, exchangeRate, logoPath, period, startDate, endDate }, feesObj) => {
  const doc = new jsPDF();
  const logoBase64 = logoPath ? await loadLogoAsBase64(logoPath) : null;
  const rangeLabel = getDateRangeLabel(period, startDate, endDate);

  addLetterhead(doc, logoBase64, {
    title: 'ACCOUNT STATEMENT',
    subtitle: `${sellerInfo?.displayName || sellerInfo?.fullName || 'Seller'} • ${rangeLabel}`
  });

  let yPos = 58;
  // Each order is billed at the rate recorded when it was placed, so a
  // statement spanning a fee change still adds up.
  const feeOf = (o) => {
    const gross = (o.summary?.subtotal || 0) + (o.summary?.deliveryFee || 0);
    return gross * (orderSellerFeePercent(o, feesObj?.seller) / 100);
  };

  const rows = orders.map((o) => {
    const gross = (o.summary?.subtotal || 0) + (o.summary?.deliveryFee || 0);
    const fee = feeOf(o);
    const net = gross - fee;
    const typeLabel = o.productInfo?.listingType === 'service' ? 'Service' : 'Product';
    return [
      o.customOrderId,
      new Date(o.createdAt).toLocaleDateString(),
      o.buyerInfo?.fullName || 'N/A',
      `${o.productInfo?.title || 'N/A'} (${typeLabel})`,
      formatCurrency(gross, currency, exchangeRate),
      formatCurrency(fee, currency, exchangeRate),
      formatCurrency(net, currency, exchangeRate)
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['Order #', 'Date', 'Buyer', 'Product', 'Gross', 'Platform Fee', 'Net']],
    body: rows,
    theme: 'striped',
    ...tableTheme,
    alternateRowStyles: { fillColor: [252, 250, 248] },
  });

  const totalGross = orders.reduce((s, o) => s + (o.summary?.subtotal || 0) + (o.summary?.deliveryFee || 0), 0);
  const totalFee = orders.reduce((s, o) => s + feeOf(o), 0);
  const totalNet = totalGross - totalFee;

  yPos = doc.lastAutoTable.finalY + 10;
  doc.setDrawColor(...BORDER_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, yPos - 4, PAGE_WIDTH - MARGIN, yPos - 4);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`Total Orders: ${orders.length}`, MARGIN, yPos + 4);
  doc.text(`Total Net Earnings: ${formatCurrency(totalNet, currency, exchangeRate)}`, 120, yPos + 4);

  addFooter(doc, `Chocowi Account Statement. Generated on ${new Date().toLocaleDateString()}`);

  doc.save(`Chocowi-AccountStatement-${rangeLabel.replace(/\s/g, '-')}.pdf`);
};

export const generatePayoutStatement = async (paymentHistory, sellerInfo, { currency, exchangeRate, logoPath, period, startDate, endDate }) => {
  const doc = new jsPDF();
  const logoBase64 = logoPath ? await loadLogoAsBase64(logoPath) : null;
  const rangeLabel = getDateRangeLabel(period, startDate, endDate);

  addLetterhead(doc, logoBase64, {
    title: 'PAYOUT STATEMENT',
    subtitle: `${sellerInfo?.displayName || sellerInfo?.fullName || 'Seller'} • ${rangeLabel}`
  });

  let yPos = 58;
  const rows = paymentHistory.map((h) => [
    h.description || '-',
    h.orderIdCustom || '-',
    h.buyerUsername || '-',
    new Date(h.date).toLocaleDateString(),
    h.paymentMethod || '-',
    formatCurrency(h.amount, currency, exchangeRate)
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Activity', 'Order #', 'Buyer', 'Date', 'Method', 'Amount']],
    body: rows,
    theme: 'striped',
    ...tableTheme,
    alternateRowStyles: { fillColor: [252, 250, 248] },
  });

  const totalEarnings = paymentHistory.filter((h) => h.status === 'Earning').reduce((s, h) => s + h.amount, 0);
  const totalWithdrawn = paymentHistory.filter((h) => h.status === 'Withdraw' || h.status === 'Paid').reduce((s, h) => s + h.amount, 0);

  yPos = doc.lastAutoTable.finalY + 10;
  doc.setDrawColor(...BORDER_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, yPos - 4, PAGE_WIDTH - MARGIN, yPos - 4);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`Total Earnings: ${formatCurrency(totalEarnings, currency, exchangeRate)}`, MARGIN, yPos + 4);
  doc.text(`Total Payouts: ${formatCurrency(totalWithdrawn, currency, exchangeRate)}`, 120, yPos + 4);

  addFooter(doc, `Chocowi Payout Statement. Generated on ${new Date().toLocaleDateString()}`);

  doc.save(`Chocowi-PayoutStatement-${rangeLabel.replace(/\s/g, '-')}.pdf`);
};

export const generateExpenseStatement = async (orders, buyerInfo, { currency, exchangeRate, logoPath, period, startDate, endDate }) => {
  const doc = new jsPDF();
  const logoBase64 = logoPath ? await loadLogoAsBase64(logoPath) : null;
  const rangeLabel = getDateRangeLabel(period, startDate, endDate);

  addLetterhead(doc, logoBase64, {
    title: 'EXPENSE STATEMENT',
    subtitle: `${buyerInfo?.username || buyerInfo?.fullName || 'Buyer'} • ${rangeLabel}`
  });

  let yPos = 58;
  const rows = orders.map((o) => {
    const typeLabel = o.productInfo?.listingType === 'service' ? 'Service' : 'Product';
    return [
      o.customOrderId,
      new Date(o.createdAt).toLocaleDateString(),
      o.sellerId?.displayName || 'N/A',
      `${o.productInfo?.title || 'N/A'} (${typeLabel})`,
      o.summary?.quantity || 1,
      formatCurrency(o.summary?.total || 0, currency, exchangeRate)
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['Order #', 'Date', 'Seller', 'Product', 'Qty', 'Amount']],
    body: rows,
    theme: 'striped',
    ...tableTheme,
    alternateRowStyles: { fillColor: [252, 250, 248] },
  });

  const total = orders.reduce((s, o) => s + (o.summary?.total || 0), 0);
  yPos = doc.lastAutoTable.finalY + 10;
  doc.setDrawColor(...BORDER_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, yPos - 4, PAGE_WIDTH - MARGIN, yPos - 4);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`Total Purchases: ${orders.length}`, MARGIN, yPos + 4);
  doc.text(`Total Amount: ${formatCurrency(total, currency, exchangeRate)}`, 120, yPos + 4);

  addFooter(doc, 'Chocowi Expense Statement. For personal/business record keeping.');

  doc.save(`Chocowi-ExpenseStatement-${rangeLabel.replace(/\s/g, '-')}.pdf`);
};
